import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Bell, Settings, Search, Inbox, AlertTriangle, RefreshCw,
  AlertOctagon, Info, CheckCircle2, Clock, User, Send
} from 'lucide-react';
import { doc, getDocs, updateDoc, collection } from 'firebase/firestore';
import { auth, db } from '../../firebase';

// ====================================================================================
// CONSTANTS & HELPERS
// ====================================================================================

const MESSAGE_CONTEXTS = {
  update:       { label: 'Update',    icon: RefreshCw,     color: 'text-blue-400',    bg: 'bg-blue-500/10',    border: 'border-blue-500/20',    accent: 'rgba(96,165,250,1)'   },
  warning:      { label: 'Warning',   icon: AlertTriangle, color: 'text-amber-400',   bg: 'bg-amber-500/10',   border: 'border-amber-500/20',   accent: 'rgba(251,191,36,1)'   },
  alert:        { label: 'Alert',     icon: AlertOctagon,  color: 'text-rose-400',    bg: 'bg-rose-500/10',    border: 'border-rose-500/20',    accent: 'rgba(251,113,133,1)'  },
  general:      { label: 'General',   icon: Info,          color: 'text-slate-400',   bg: 'bg-slate-500/10',   border: 'border-slate-500/20',   accent: 'rgba(148,163,184,1)'  },
  notification: { label: 'Notice',    icon: Bell,          color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', accent: 'rgba(52,211,153,1)'   },
};

// Status config — colours match admin ToolboxTickets exactly
const STATUS_DOT = {
  open:     { cls: 'bg-blue-400',    glow: 'shadow-[0_0_6px_rgba(96,165,250,0.9)]',  label: 'Open',     textColor: 'text-blue-400',    bg: 'bg-blue-600/10',    border: 'border-blue-500/30'    },
  pending:  { cls: 'bg-amber-400',   glow: 'shadow-[0_0_6px_rgba(251,191,36,0.9)]',  label: 'Pending',  textColor: 'text-amber-400',   bg: 'bg-amber-600/10',   border: 'border-amber-500/30'   },
  resolved: { cls: 'bg-emerald-400', glow: 'shadow-[0_0_6px_rgba(52,211,153,0.9)]',  label: 'Resolved', textColor: 'text-emerald-400', bg: 'bg-emerald-600/10', border: 'border-emerald-500/30' },
  closed:   { cls: 'bg-slate-500',   glow: '',                                         label: 'Closed',   textColor: 'text-slate-400',   bg: 'bg-slate-700/50',   border: 'border-slate-600/50'   },
};

const formatDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
};

const formatTime = (ts) => {
  if (!ts) return '';
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  const diff = Date.now() - d;
  if (diff < 60000)     return 'just now';
  if (diff < 3600000)   return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000)  return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

// Initials avatar for a sender string
const initials = (str = '') =>
  str.split(/[\s@.]+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('') || '?';

// ====================================================================================
// PULSE INPUT HOOK — from LocalIssuesPortal
// ====================================================================================
const usePulse = () => {
  const [isPulsing, setIsPulsing] = useState(false);
  const timer = useRef(null);
  const trigger = () => {
    setIsPulsing(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { setIsPulsing(false); }, 150);
  };
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  return [isPulsing, trigger];
};

// ====================================================================================
// SYSTEM MESSAGE LIST ITEM
// ====================================================================================
const MessageListItem = ({ message, isSelected, onClick }) => {
  const theme = MESSAGE_CONTEXTS[message.context] || MESSAGE_CONTEXTS.general;
  const isUnread = message.status === 'active';

  return (
    <div onClick={() => onClick(message)}
      className={`group relative px-4 py-3.5 cursor-pointer transition-all duration-150 border-b border-slate-800/40
        ${isSelected ? 'bg-white/[0.06]' : 'hover:bg-white/[0.03]'}`}>
      {/* Left accent bar */}
      {isSelected && (
        <div className="absolute left-0 top-2 bottom-2 w-[3px] rounded-full bg-white shadow-[0_0_8px_rgba(255,255,255,0.6)]"/>
      )}
      <div className="flex items-start gap-3 pl-1">
        {/* Coloured left accent dot instead of icon */}
        <div className="mt-1.5 flex-shrink-0 w-2 h-2 rounded-full" style={{background: theme.accent, boxShadow:`0 0 6px ${theme.accent}60`}}/>
        <div className="flex-grow min-w-0">
          <div className="flex justify-between items-baseline gap-2 mb-0.5">
            <h4 className={`text-sm font-semibold truncate leading-snug ${isSelected ? 'text-white' : 'text-slate-200'}`}>
              {message.header}
            </h4>
            <span className="text-[10px] text-slate-600 font-mono shrink-0">{formatDate(message.timestamp)}</span>
          </div>
          <p className="text-xs text-slate-400 line-clamp-1 leading-relaxed">{message.body}</p>
          <div className="flex items-center gap-2 mt-1.5">
            <span className={`text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full border ${theme.border} ${theme.bg} ${theme.color}`}>
              {theme.label}
            </span>
            {isUnread && <span className="w-1.5 h-1.5 rounded-full bg-white shadow-[0_0_6px_rgba(255,255,255,0.8)] animate-pulse"/>}
          </div>
        </div>
      </div>
    </div>
  );
};

// ====================================================================================
// TICKET LIST ITEM
// ====================================================================================
const TicketListItem = ({ ticket, isSelected, onClick }) => {
  const lastMsg = ticket.messages?.[ticket.messages.length - 1];
  const preview = typeof lastMsg === 'string' ? lastMsg : (lastMsg?.text || '');
  const dot = STATUS_DOT[ticket.status] || STATUS_DOT.open;
  const count = ticket.messages?.length || 0;
  const hasUnread = ticket.status === 'open' && count > 0;

  return (
    <div onClick={() => onClick(ticket)}
      className={`group relative px-4 py-3.5 cursor-pointer transition-all duration-150 border-b
        ${isSelected ? 'bg-white/[0.06]' : 'hover:bg-white/[0.025]'}`}
      style={{borderColor:'rgba(255,255,255,0.05)'}}>
      {isSelected && (
        <div className="absolute left-0 top-2.5 bottom-2.5 w-[3px] rounded-full bg-white"
          style={{boxShadow:'0 0 10px rgba(255,255,255,0.7)'}}/>
      )}
      <div className="flex items-start gap-3 pl-1">
        <div className="flex flex-col items-center gap-1.5 pt-1.5 flex-shrink-0">
          <div className={`w-2 h-2 rounded-full ${dot.cls} ${dot.glow}`}/>
        </div>
        <div className="flex-grow min-w-0">
          <div className="flex justify-between items-baseline gap-2 mb-1">
            <h4 className={`text-[13px] font-semibold truncate leading-snug tracking-tight
              ${isSelected ? 'text-white' : 'text-slate-200 group-hover:text-white transition-colors'}`}>
              {ticket.subject}
            </h4>
            <span className="text-[10px] font-mono shrink-0" style={{color:'rgba(100,116,139,0.8)'}}>
              {formatTime(ticket.createdAt)}
            </span>
          </div>
          {preview && (
            <p className="text-[11px] line-clamp-1 leading-relaxed" style={{color:'rgba(148,163,184,0.85)'}}>
              {preview}
            </p>
          )}
          <div className="flex items-center justify-between mt-1.5">
            <span className="text-[10px]" style={{color:'rgba(100,116,139,0.9)'}}>
              {count === 0 ? 'No messages yet' : `${count} ${count === 1 ? 'message' : 'messages'}`}
            </span>
            {hasUnread && (
              <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 shadow-[0_0_6px_rgba(129,140,248,0.8)]"/>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ====================================================================================
// SYSTEM MESSAGE DETAIL
// ====================================================================================
const MessageDetail = ({ message }) => {
  if (!message) return (
    <div className="h-full flex flex-col items-center justify-center gap-3 select-none">
      <div className="w-16 h-16 rounded-2xl flex items-center justify-center"
        style={{background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.07)'}}>
        <Inbox size={28} className="text-slate-600"/>
      </div>
      <p className="text-sm text-slate-600 font-medium">Select a notification</p>
    </div>
  );

  const theme = MESSAGE_CONTEXTS[message.context] || MESSAGE_CONTEXTS.general;
  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="px-8 py-6 border-b border-slate-800/60 shrink-0">
        <div className="flex items-center gap-2 mb-3">
          <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border ${theme.border} ${theme.bg} ${theme.color}`}>
            {theme.label}
          </span>
          <span className="text-[10px] text-slate-600 font-mono">{formatDate(message.timestamp)}</span>
        </div>
        <h2 className="text-xl font-bold text-white leading-tight mb-1">{message.header}</h2>
        {message.author && (
          <p className="text-xs text-slate-500 flex items-center gap-1.5">
            <User size={11} className="text-slate-600"/> From <span className="text-slate-400">{message.author}</span>
          </p>
        )}
      </div>
      {/* Body */}
      <div className="flex-1 overflow-y-auto custom-scrollbar px-8 py-6">
        <div className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap">{message.body}</div>
      </div>
      <div className="px-8 py-3 border-t border-slate-800/60 shrink-0">
        <span className="text-[10px] text-slate-700 font-mono">REF: {message.id}</span>
      </div>
    </div>
  );
};

// ====================================================================================
// TICKET CHAT WINDOW
// ====================================================================================
const TicketChatWindow = ({ ticket }) => {
  const [messages, setMessages]   = useState(ticket?.messages ?? []);
  const [inputText, setInputText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [showInfo, setShowInfo]   = useState(false);
  const bottomRef   = useRef(null);
  const textareaRef = useRef(null);
  const [isPulsing, triggerPulse] = usePulse();

  useEffect(() => { setMessages(ticket?.messages ?? []); }, [ticket]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleSend = useCallback(async () => {
    const text = inputText.trim();
    if (!text || isSending || !ticket) return;
    setIsSending(true);
    const updatedMessages = [...(ticket.messages ?? []), text];
    setMessages(updatedMessages);
    setInputText('');
    if (textareaRef.current) { textareaRef.current.style.height = 'auto'; }
    try {
      await updateDoc(
        doc(db, 'users', auth.currentUser.uid, 'tickets', ticket.id),
        { messages: updatedMessages }
      );
    } catch (err) {
      console.error('Failed to send:', err);
      setMessages(ticket.messages ?? []);
      setInputText(text);
    } finally {
      setIsSending(false);
    }
  }, [inputText, isSending, ticket]);

  if (!ticket) return (
    <div className="h-full flex flex-col items-center justify-center gap-3 select-none">
      <div className="w-16 h-16 rounded-2xl flex items-center justify-center"
        style={{background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.07)'}}>
        <Inbox size={28} className="text-slate-600"/>
      </div>
      <p className="text-sm text-slate-600 font-medium">Select a ticket</p>
    </div>
  );

  const dot = STATUS_DOT[ticket.status] || STATUS_DOT.open;
  const userEmail = auth.currentUser?.email || 'You';
  const userInitials = initials(userEmail);

  return (
    <div className="h-full flex flex-col">

      {/* ── Header: subject + status dot + ⓘ info toggle ── */}
      <div className="shrink-0" style={{borderBottom:'1px solid rgba(255,255,255,0.07)'}}>
        <div className="px-6 py-4 flex items-center justify-between gap-4"
          style={{background:'linear-gradient(135deg, rgba(99,102,241,0.07) 0%, rgba(8,12,24,0.4) 60%)'}}>
          <div className="min-w-0 flex-1">
            <h2 className="text-[15px] font-bold text-white leading-tight truncate tracking-tight">{ticket.subject}</h2>
            <p className="text-[11px] mt-0.5" style={{color:'rgba(148,163,184,0.55)'}}>
              {messages.length} {messages.length === 1 ? 'message' : 'messages'}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            {/* Status dot — matches admin colours exactly */}
            <div className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${dot.cls} ${dot.glow}`}/>
              <span className={`text-[10px] font-bold uppercase tracking-widest ${dot.textColor}`}>{dot.label}</span>
            </div>
            {/* Info toggle button */}
            <button
              onClick={() => setShowInfo(v => !v)}
              title="Ticket details"
              className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-200 select-none
                ${showInfo
                  ? 'bg-white/15 text-white shadow-[0_0_10px_rgba(255,255,255,0.15)]'
                  : 'text-slate-500 hover:text-slate-300 hover:bg-white/8'
                }`}
              style={{border:'1px solid rgba(255,255,255,0.1)', fontStyle:'italic', fontFamily:'Georgia, serif', fontSize:'13px'}}>
              i
            </button>
          </div>
        </div>

        {/* Info drawer — slides down when showInfo is true */}
        <AnimatePresence>
          {showInfo && (
            <motion.div
              key="info"
              initial={{height:0, opacity:0}}
              animate={{height:'auto', opacity:1}}
              exit={{height:0, opacity:0}}
              transition={{duration:0.22, ease:[0.32,0.72,0,1]}}
              style={{overflow:'hidden'}}
            >
              <div className="px-6 py-4 grid grid-cols-2 gap-x-8 gap-y-3"
                style={{background:'rgba(6,8,18,0.6)', borderBottom:'1px solid rgba(255,255,255,0.06)'}}>
                <div>
                  <p className="text-[9px] font-bold uppercase tracking-widest text-slate-600 mb-1">Ticket ID</p>
                  <p className="text-xs font-mono text-slate-400 truncate">{ticket.id}</p>
                </div>
                <div>
                  <p className="text-[9px] font-bold uppercase tracking-widest text-slate-600 mb-1">Status</p>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${dot.border} ${dot.bg} ${dot.textColor}`}>
                    {dot.label}
                  </span>
                </div>
                <div>
                  <p className="text-[9px] font-bold uppercase tracking-widest text-slate-600 mb-1">Submitted</p>
                  <p className="text-xs text-slate-400">{formatDate(ticket.createdAt)}</p>
                </div>
                <div>
                  <p className="text-[9px] font-bold uppercase tracking-widest text-slate-600 mb-1">Messages</p>
                  <p className="text-xs text-slate-400">{messages.length}</p>
                </div>
                {ticket.subject && (
                  <div className="col-span-2">
                    <p className="text-[9px] font-bold uppercase tracking-widest text-slate-600 mb-1">Subject</p>
                    <p className="text-xs text-slate-300 leading-relaxed">{ticket.subject}</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ── Message Thread ── */}
      <div className="flex-1 overflow-y-auto custom-scrollbar px-6 py-6 space-y-4"
        style={{background:'linear-gradient(180deg, rgba(8,12,24,0) 0%, rgba(6,8,18,0.3) 100%)'}}>
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center gap-4 select-none">
            {/* Decorative concentric rings */}
            <div className="relative flex items-center justify-center">
              <div className="absolute w-24 h-24 rounded-full" style={{border:'1px solid rgba(99,102,241,0.08)'}}/>
              <div className="absolute w-16 h-16 rounded-full" style={{border:'1px solid rgba(99,102,241,0.12)'}}/>
              <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{background:'rgba(99,102,241,0.12)', border:'1px solid rgba(99,102,241,0.25)'}}>
                <Send size={16} className="text-indigo-400"/>
              </div>
            </div>
            <div className="text-center">
              <p className="text-sm font-semibold text-slate-300">Start the conversation</p>
              <p className="text-xs text-slate-600 mt-1">We typically respond within a few hours</p>
            </div>
          </div>
        ) : messages.map((msg, idx) => {
          const isString = typeof msg === 'string';
          const text     = isString ? msg : msg.text;
          const isAdmin  = !isString && msg.senderRole === 'admin';
          const ts       = isString ? null : msg.createdAt;
          const senderLabel = isAdmin ? 'Support' : 'You';
          const senderInit  = isAdmin ? 'S' : userInitials;

          // Group consecutive messages from same sender — hide avatar if next is same
          const nextMsg = messages[idx + 1];
          const nextIsAdmin = nextMsg && typeof nextMsg !== 'string' && nextMsg.senderRole === 'admin';
          const isSameNext = nextMsg && (isAdmin === nextIsAdmin);

          return (
            <div key={idx} className={`flex items-end gap-2.5 ${isAdmin ? 'flex-row' : 'flex-row-reverse'}`}
              style={{marginBottom: isSameNext ? '2px' : '12px'}}>

              {/* Avatar — only on last in a group */}
              <div className="flex-shrink-0 w-7" style={{marginBottom:'2px'}}>
                {!isSameNext && (
                  <div className={`
                    w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold select-none
                    ${isAdmin
                      ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/25'
                      : 'text-slate-400 border border-slate-700/80'
                    }
                  `} style={isAdmin ? {} : {background:'rgba(30,41,59,0.8)'}}>
                    {senderInit}
                  </div>
                )}
              </div>

              <div className={`flex flex-col gap-0.5 max-w-[70%] ${isAdmin ? 'items-start' : 'items-end'}`}>
                <div className={`
                  px-4 py-2.5 text-sm leading-relaxed font-[450] tracking-[0.01em]
                  ${isAdmin ? 'rounded-2xl rounded-bl-md' : 'rounded-2xl rounded-br-md'}
                `}
                  style={isAdmin
                    ? {
                        background:'linear-gradient(135deg, rgba(99,102,241,0.2) 0%, rgba(99,102,241,0.12) 100%)',
                        border:'1px solid rgba(99,102,241,0.25)',
                        backdropFilter:'blur(12px)',
                        color:'rgba(255,255,255,0.92)',
                      }
                    : {
                        background:'linear-gradient(135deg, rgba(30,41,59,0.95) 0%, rgba(22,32,48,0.95) 100%)',
                        border:'1px solid rgba(255,255,255,0.09)',
                        backdropFilter:'blur(12px)',
                        color:'rgba(255,255,255,0.93)',
                      }
                  }
                >
                  {text}
                </div>
                {/* Meta — only on last in group */}
                {!isSameNext && (
                  <div className={`flex items-center gap-1 px-1 ${isAdmin ? '' : 'flex-row-reverse'}`}>
                    <span className="text-[10px] text-slate-500 font-medium">{senderLabel}</span>
                    {ts && (
                      <>
                        <span className="text-[10px] text-slate-700">·</span>
                        <span className="text-[10px] text-slate-500">{formatTime(ts)}</span>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef}/>
      </div>

      {/* ── Input bar — clean, no avatar token ── */}
      <div className="px-5 pt-3 pb-4 shrink-0"
        style={{borderTop:'1px solid rgba(255,255,255,0.07)', background:'rgba(8,12,24,0.8)', backdropFilter:'blur(20px)'}}>
        <div className={`
          flex items-center gap-2 rounded-2xl px-4 transition-all duration-200
          ${isPulsing
            ? 'shadow-[0_0_24px_rgba(255,255,255,0.1)] border-white/50'
            : 'focus-within:shadow-[0_0_20px_rgba(99,102,241,0.12)] focus-within:border-indigo-500/40'
          }
        `} style={{
          border: isPulsing ? '1px solid rgba(255,255,255,0.5)' : '1px solid rgba(255,255,255,0.08)',
          background:'rgba(15,23,42,0.9)',
          minHeight: '52px',
        }}>
          <textarea
            ref={textareaRef}
            rows={1}
            value={inputText}
            placeholder="Write a message…"
            onChange={e => {
              setInputText(e.target.value);
              triggerPulse();
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
            }}
            className="flex-1 bg-transparent text-sm text-white placeholder-slate-500 outline-none resize-none leading-6 py-3.5"
            style={{minHeight:'24px', maxHeight:'120px'}}
          />
          <button onClick={handleSend}
            disabled={!inputText.trim() || isSending}
            className={`
              flex-shrink-0 rounded-xl flex items-center justify-center transition-all duration-200 self-center
              ${inputText.trim() && !isSending
                ? 'w-9 h-9 bg-white hover:bg-slate-100 text-slate-900 shadow-lg shadow-white/20 hover:shadow-white/30 scale-100 hover:scale-105'
                : 'w-8 h-8 bg-transparent text-slate-700 cursor-not-allowed'
              }
            `}>
            <Send size={14} className={inputText.trim() ? '' : 'opacity-30'}/>
          </button>
        </div>
        <p className="text-[10px] text-slate-700 mt-2 text-center tracking-wider select-none">
          Enter to send · Shift+Enter for new line
        </p>
      </div>
    </div>
  );
};

// ====================================================================================
// MAIN COMPONENT
// ====================================================================================
export const NotificationsPage = ({ notifications = [] }) => {
  const [activeTab, setActiveTab]             = useState('system');
  const [tickets, setTickets]                 = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(notifications?.[0] ?? null);
  const [selectedTicket, setSelectedTicket]   = useState(null);
  const [searchQuery, setSearchQuery]         = useState('');

  // Fetch tickets — identical to original
  useMemo(() => {
    getDocs(collection(db, 'users', auth.currentUser.uid, 'tickets')).then(({ docs }) => {
      const mapped = docs.map(d => ({ id: d.id, ...d.data() }));
      setTickets(mapped);
      if (mapped.length > 0) setSelectedTicket(mapped[0]);
    });
  }, [auth.currentUser.uid, activeTab]);

  const filteredMessages = useMemo(() => {
    const q = searchQuery.toLowerCase();
    if (activeTab === 'system') {
      return notifications.filter(m => !q || m.header?.toLowerCase().includes(q) || m.body?.toLowerCase().includes(q));
    }
    return tickets.filter(t => !q || t.subject?.toLowerCase().includes(q));
  }, [activeTab, notifications, tickets, searchQuery]);

  const systemCount  = notifications.filter(n => n.status === 'active').length;
  const ticketCount  = tickets.length;

  return (
    <div className="h-full p-4 lg:p-8 flex flex-col gap-6">

      {/* ── Page Header ── */}
      <div>
        <h1 className="text-xl font-bold text-white tracking-tight">Inbox</h1>
        <p className="text-xs mt-0.5" style={{color:'rgba(148,163,184,0.5)'}}>Notifications and support conversations</p>
      </div>

      {/* ── Main Container ── */}
      <div className="flex-1 min-h-0 flex rounded-2xl overflow-hidden shadow-2xl"
        style={{border:'1px solid rgba(255,255,255,0.07)', background:'rgba(8,12,24,0.7)', backdropFilter:'blur(24px)'}}>

        {/* ── LEFT PANEL ── */}
        <div className="w-full lg:w-72 xl:w-80 border-r flex flex-col shrink-0"
          style={{borderColor:'rgba(255,255,255,0.07)', background:'rgba(10,15,28,0.6)'}}>

          {/* Tabs — no icons */}
          <div className="flex border-b shrink-0" style={{borderColor:'rgba(255,255,255,0.07)'}}>
            {[
              { id: 'system',  label: 'Notifications', count: systemCount  },
              { id: 'tickets', label: 'Support',        count: ticketCount  },
            ].map(({ id, label, count }) => {
              const isActive = activeTab === id;
              return (
                <button key={id} onClick={() => setActiveTab(id)}
                  className={`flex-1 py-3.5 relative flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-widest transition-all duration-200 outline-none
                    ${isActive ? 'text-white' : 'text-slate-600 hover:text-slate-400'}`}>
                  <span>{label}</span>
                  {count > 0 && (
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none
                      ${isActive ? 'bg-white/15 text-white' : 'bg-slate-800 text-slate-500'}`}>
                      {count}
                    </span>
                  )}
                  {isActive && (
                    <motion.div layoutId="tabLine"
                      className="absolute bottom-0 left-3 right-3 h-[2px] rounded-full bg-white shadow-[0_0_8px_rgba(255,255,255,0.5)]"
                      transition={{ type: 'spring', stiffness: 500, damping: 35 }}/>
                  )}
                </button>
              );
            })}
          </div>

          {/* Search — no icon badge */}
          <div className="p-3 border-b shrink-0" style={{borderColor:'rgba(255,255,255,0.06)'}}>
            <div className="relative">
              <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600"/>
              <input type="text" placeholder="Search…" value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full py-1.5 pl-8 pr-3 text-xs text-slate-300 rounded-lg outline-none transition-all placeholder:text-slate-700
                  focus:shadow-[0_0_10px_rgba(255,255,255,0.06)]"
                style={{background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.07)'}}
              />
            </div>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto custom-scrollbar">
            {filteredMessages.length > 0 ? (
              activeTab === 'system'
                ? filteredMessages.map(msg => (
                    <MessageListItem key={msg.id} message={msg}
                      isSelected={selectedMessage?.id === msg.id} onClick={setSelectedMessage}/>
                  ))
                : filteredMessages.map(ticket => (
                    <TicketListItem key={ticket.id} ticket={ticket}
                      isSelected={selectedTicket?.id === ticket.id} onClick={setSelectedTicket}/>
                  ))
            ) : (
              <div className="p-8 flex flex-col items-center justify-center gap-3 text-center select-none">
                <CheckCircle2 size={20} className="text-slate-700"/>
                <p className="text-xs text-slate-600">
                  {activeTab === 'system' ? 'No notifications' : 'No support tickets'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* ── RIGHT PANEL ── */}
        <div className="hidden lg:flex flex-1 flex-col min-h-0">
          <AnimatePresence mode="wait">
            {activeTab === 'system' ? (
              <motion.div key="system" className="flex-1 h-full"
                initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} transition={{duration:0.12}}>
                <MessageDetail message={selectedMessage}/>
              </motion.div>
            ) : (
              <motion.div key={`ticket-${selectedTicket?.id}`} className="flex-1 h-full"
                initial={{opacity:0, y:6}} animate={{opacity:1, y:0}} exit={{opacity:0}} transition={{duration:0.18}}>
                <TicketChatWindow ticket={selectedTicket}/>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

      </div>
    </div>
  );
};

export default NotificationsPage;